<template>
  <div>
    <div class="container-fluid">
      <div class="row fme-new-edit__title fme-montserrat-font">
        <div class="col-md-10">
          <h3>
            {{
              $t(`supervision.crud.page.${crudCurrentPageTitle}`)
                | textUpperCase
            }}
          </h3>
        </div>

        <div class="col-md-2 text-right">
          <BaseButtonEvent
            :btText="$t('supervision.crud.button.goBack')"
            btClickType="goBack"
            btId="newEditeGoBack"
            btClickValue=""
            btUrlGoBack="CrudLocationListPage"
            btCss="btn fme-btn fme-btn-secondary"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="row fme-new-edit__row">
            <div class="col-md-4">
              <BaseInputAutocompleteSettingsRegion
                :inputCurrentValue="locationRegion.name"
                :inputTitle="$t('supervision.crud.form.location.region.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.region.placeholder')
                "
                :inputUrl="regionAutocompleteUrl"
                inputId="input-location-region"
                @returned-input-autocomplete-value="
                  locationInput('region', $event)
                "
                :inputErrorText="$t(locationError.region)"
              />
            </div>
            <div class="col-md-8">
              <BaseInput
                :inputCurrentValue="locationName"
                :inputTitle="$t('supervision.crud.form.location.name.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.name.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-name"
                @returned-input-value="locationInput('name', $event)"
                :inputErrorText="locationError.name ? $t(locationError.name) : $t(locationError.role)"
              />
            </div>
          </div>
          <div class="row fme-new-edit__row">
            <div class="col-md-8">
              <TheInputMap
                :inputCurrentValue="locationAddress"
                :inputTitle="$t('supervision.crud.form.location.address.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.address.placeholder')
                "
                inputType="address"
                inputCss=""
                inputId="input-location-address"
                @returned-input-value="locationInput('address', $event)"
                @returned-map-new-address="locationInput('newAddress', $event)"
                :inputErrorText="$t(locationError.address)"
                :inputCurrentLat="locationLatitude"
                :inputCurrentLng="locationLongitude"
              />
            </div>
            <div class="col-md-4">
              <BaseInputAutocompleteSettingsCity
                :inputCurrentValue="locationCity.city"
                :inputTitle="$t('supervision.crud.form.location.city.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.city.placeholder')
                "
                :inputUrl="cityAutocompleteUrl"
                inputId="input-location-city"
                @returned-input-autocomplete-value="
                  locationInput('city', $event)
                "
                :inputErrorText="$t(locationError.city)"
              />
            </div>
          </div>

          <div class="row fme-new-edit__row">
            <div class="col-md-2">
              <BaseInput
                :inputCurrentValue="locationLatitude"
                :inputTitle="
                  $t('supervision.crud.form.location.latitude.title')
                "
                :inputPlaceholder="
                  $t('supervision.crud.form.location.latitude.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-latitude"
                @returned-input-value="locationInput('latitude', $event)"
                :inputErrorText="$t(locationError.latitude)"
                :inputDecimalNumber="true"
              />
            </div>

            <div class="col-md-2">
              <BaseInput
                :inputCurrentValue="locationLongitude"
                :inputTitle="
                  $t('supervision.crud.form.location.longitude.title')
                "
                :inputPlaceholder="
                  $t('supervision.crud.form.location.longitude.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-longitude"
                @returned-input-value="locationInput('longitude', $event)"
                :inputErrorText="$t(locationError.longitude)"
                :inputDecimalNumber="true"
              />
            </div>

            <div class="col-md-2">
              <BaseInput
                :inputCurrentValue="locationRadius"
                :inputTitle="$t('supervision.crud.form.location.radius.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.radius.placeholder')
                "
                inputType="number"
                inputCss=""
                inputId="input-location-radius"
                @returned-input-value="locationInput('radius', $event)"
                :inputErrorText="$t(locationError.radius)"
              />
            </div>

            <div class="col-md-2">
              <BaseInput
                :inputCurrentValue="locationAutoFinishRadius"
                :inputTitle="
                  $t('supervision.crud.form.location.autoFinishRadius.title')
                "
                :inputPlaceholder="
                  $t(
                    'supervision.crud.form.location.autoFinishRadius.placeholder'
                  )
                "
                inputType="number"
                inputCss=""
                inputId="input-location-auto-finish-radius"
                @returned-input-value="
                  locationInput('autoFinishRadius', $event)
                "
                :inputErrorText="$t(locationError.autoFinishRadius)"
              />
            </div>

            <div class="col-md-4">
              <BaseSelect
                selectId="select-timeZone"
                :selectTitle="
                  $t('supervision.crud.form.location.timeZone.title')
                "
                :selectValues="selectTimeZoneItems"
                selectCss=""
                :selectCurrentValue="locationTimeZone"
                @returnedSelectValue="locationSelect('timeZone', $event)"
                :selectErrorText="$t(locationError.timeZone)"
                :selectReticence="enableReticence"
              />
            </div>

            <div class="col-md-12">
              <input
                type="checkbox"
                id="checkbox-location-geolocation-adjustment"
                v-model="locationGeolocationAdjustment"
              />
              <label
                for="checkbox-location-geolocation-adjustment"
                style="white-space: pre"
              >
                {{
                  $t(
                    "supervision.crud.form.location.geolocationAdjustment.placeholder"
                  )
                }}</label
              >
            </div>
          </div>

          <div class="row fme-new-edit__row">
            <div class="col-md-4">
              <BaseInput
                :inputCurrentValue="locationPassword"
                :inputTitle="
                  $t('supervision.crud.form.location.password.title')
                "
                :inputPlaceholder="
                  $t('supervision.crud.form.location.password.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-password"
                @returned-input-value="locationInput('password', $event)"
              />
            </div>

            <div class="col-md-4">
              <BaseInput
                :inputCurrentValue="locationCountersign"
                :inputTitle="
                  $t('supervision.crud.form.location.counterpass.title')
                "
                :inputPlaceholder="
                  $t('supervision.crud.form.location.counterpass.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-counterpass"
                @returned-input-value="locationInput('counterpass', $event)"
              />
            </div>

            <div class="col-md-4">
              <BaseInput
                :inputCurrentValue="locationSecurityPhone"
                :inputTitle="$t('supervision.crud.form.location.phone.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.phone.placeholder')
                "
                inputType="text"
                inputCss=""
                inputId="input-location-phone"
                @returned-input-value="locationInput('securityPhone', $event)"
              />
            </div>
          </div>

          <div class="row fme-new-edit__row">
            <div class="col-md-4">
              <BaseInputAutocompleteSettingsRegion
                :inputCurrentValue="locationClient.name"
                :inputTitle="$t('supervision.crud.form.location.client.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.client.placeholder')
                "
                :inputUrl="clientAutocompleteUrl"
                inputId="input-location-client"
                @returned-input-autocomplete-value="
                  locationInput('client', $event)
                "
                :inputErrorText="$t(locationError.client)"
              />
            </div>

            <!--<div class="col-md-6">
              <DoubleInput
                :inputOneCurrentValue="contactDefaultName"
                :inputTwoCurrentValue="contactDefaultPhone"
                :inputOneTitle="
                  $t('supervision.crud.form.location.contact.name.title')
                "
                :inputTwoTitle="
                  $t('supervision.crud.form.location.contact.phone.title')
                "
                :inputOnePlaceholder="
                  $t('supervision.crud.form.location.contact.name.placeholder')
                "
                inputTwoPlaceholder="(00) 00000-0000"
                inputOneType="text"
                inputTwoType="text"
                inputOneCss=""
                inputCssTwo=""
                inputOneId="input-location-i"
                inputTwoId="input-location-c"
                @returned-double-input-value="locationInput('contact', $event)"
                :inputClear="true"
              />

              <ul class="fme-unordered-list__ul">
                <BaseUnorderedListDobleClickTwoValues
                  v-for="(item, index) in locationContacts"
                  :key="index"
                  :value="item"
                  :index="index"
                  @returned-unorderedlist-two-value="updateContacts($event)"
                  @returned-unorderedlist-remove-two-value="
                    deleteContact($event)
                  "
                  nameFieldOne="name"
                  nameFieldTwo="contact"
                  unorderedListType="create"
                />
              </ul>
            </div>-->
          </div>

          <!--<div class="row fme-new-edit__row">
            <div class="col-md-6">
              <BaseInputAutocomplete
                inputCurrentValue=""
                :inputTitle="$t('supervision.crud.form.location.checklist.title')"
                :inputPlaceholder="
                  $t('supervision.crud.form.location.checklist.placeholder')
                "
                :inputUrl="checklistAutocompleteUrl"
                inputId="input-location-checklist"
                @returned-input-autocomplete-value="
                  locationInput('checklist', $event)
                "
                :inputErrorText="$t(locationError.checklist)"
              />

              <ul class="fme-unordered-list__ul fme-montserrat-font">
                <li v-for="(item, index) in locationChecklist" :key="index">
                  {{ item.name }}

                  <a
                    @click.prevent="deleteChecklist(index)"
                    :title="$t('supervision.crud.button.delete')"
                    class="fme-unordered-list__li--trash"
                  >
                    <font-awesome-icon
                      far
                      icon="trash-alt"
                      class="fme-unordered-list__li--trash-icon"
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div> -->
        </div>
      </div>

      <div class="row fme-new-edit__row--last text-right">
        <div class="col-md-12">
          <BaseButtonEvent
            :btText="$t('supervision.crud.button.save')"
            :btClickType="saveValues.type"
            :btClickValue="newLocation"
            :btId="saveValues.uuid"
            :btUrlGo="saveValues.urlGo"
            :btUrlGoBack="saveValues.urlBack"
            btCss="btn fme-btn fme-btn-primary"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

const BaseInput = () => import("@/components/fragment/input/BaseInput");
const BaseSelect = () => import("@/components/fragment/select/BaseSelect");
const BaseButtonEvent = () =>
  import("@/components/fragment/button/BaseButtonEvent");
const BaseInputAutocomplete = () =>
  import("@/components/fragment/input/BaseInputAutocomplete");
const BaseInputAutocompleteSettingsCity = () =>
  import("@/components/fragment/input/BaseInputAutocompleteSettingsCity");
const BaseInputAutocompleteSettingsRegion = () =>
  import("@/components/fragment/input/BaseInputAutocompleteSettingsRegion");
const DoubleInput = () => import("@/components/fragment/input/DoubleInput");
const BaseUnorderedListDobleClickTwoValues = () =>
  import("@/components/fragment/list/BaseUnorderedListDobleClickTwoValues");
const TheInputMap = () => import("@/components/fragment/map/TheInputMap");

export default {
  name: "NewPage",
  created() {
    this.clearStore();
    this.changeCrudCurrentPageTitle("location");
    this.changeLocationAddressCrud("");
    this.changeLocationLatitudeCrud(-23.57021);
    this.changeLocationLongitudeCrud(-46.64599);
    this.changeLocationRadiusCrud(100);
    this.changeLocationAutoFinishRadiusCrud(300);
    this.changeLocationGeolocationAdjustmentCrud(false);
  },
  updated() {},
  beforeDestroy() {
    this.clearStore();
    this.changeErrors([]);
  },
  data() {
    return {
      enableReticence: false,
      contactDefaultName: "",
      contactDefaultPhone: "",
      selectTimeZoneItems: {
        default: "supervision.crud.select.timeZone.title",
        options: [
          { name: "America/Sao_Paulo", value: "timeZone.america_saoPaulo" },
          { name: "America/Recife", value: "timeZone.america_recife" },
          { name: "America/Cuiaba", value: "timeZone.america_cuiaba" },
          { name: "America/Manaus", value: "timeZone.america_manaus" },
        ],
      },
      saveValues: {
        type: "sendPostCrudLocation",
        id: "btCreateMore",
        urlGo: process.env.VUE_APP_CRUD_URL_LOCATIONS,
        urlBack: "CrudLocationListPage",
      },
      locationError: {
        name: "",
        region: "",
        address: "",
        visits: "",
        timeZone: "",
        password: "",
        counterPassword: "",
        securityPhone: "",
        latitude: "",
        longitude: "",
        geolocationAdjustment: "",
        radius: "",
        autoFinishRadius: "",
        supervisor: "",
        client: "",
        checklist: "",
        role: "",
      },
      regionAutocompleteUrl: `${process.env.VUE_APP_CRUD_URL_REGIONS}?name.like=`,
      cityAutocompleteUrl: `${process.env.VUE_APP_CRUD_URL_CITIES}?city.like=`,
      checklistAutocompleteUrl: `${process.env.VUE_APP_CHECKLISTS_API}?fields=id,uuid,name&status=active&filters=name.like.`,
      clientAutocompleteUrl: `${process.env.VUE_APP_CRUD_URL_CLIENTS}?name.like=`,
    };
  },
  props: {},
  components: {
    BaseInput,
    BaseSelect,
    BaseButtonEvent,
    BaseInputAutocomplete,
    BaseInputAutocompleteSettingsCity,
    BaseInputAutocompleteSettingsRegion,
    DoubleInput,
    BaseUnorderedListDobleClickTwoValues,
    TheInputMap,
  },
  computed: {
    ...mapGetters("ModuleErrors", ["errorsList"]),
    ...mapGetters("ModuleSupervision", [
      "locationNameCrud",
      "locationRegionCrud",
      "locationCityCrud",
      "locationAddressCrud",
      "locationQuantityVisitsMonthCrud",
      "locationLatitudeCrud",
      "locationLongitudeCrud",
      "locationGeolocationAdjustmentCrud",
      "locationRadiusCrud",
      "locationAutoFinishRadiusCrud",
      "locationTimeZoneCrud",
      "locationSupervisorCrud",
      "locationContactsCrud",
      "locationPasswordCrud",
      "locationCountersignCrud",
      "locationSecurityPhoneCrud",
      "locationClientCrud",
      "locationChecklistCrud",
      "crudCurrentPageTitle",
    ]),
    locationName() {
      return this.locationNameCrud;
    },
    locationRegion() {
      return this.locationRegionCrud;
    },
    locationCity() {
      return this.locationCityCrud;
    },
    locationAddress() {
      return this.locationAddressCrud;
    },
    locationQuantityVisitsMonth() {
      return this.locationQuantityVisitsMonthCrud;
    },
    locationLatitude() {
      return parseFloat(this.locationLatitudeCrud);
    },
    locationGeolocationAdjustment: {
      get: function () {
        return this.locationGeolocationAdjustmentCrud;
      },
      set: function (newValue) {
        this.changeLocationGeolocationAdjustmentCrud(newValue);
      },
    },
    locationLongitude() {
      return parseFloat(this.locationLongitudeCrud);
    },
    locationRadius() {
      return this.locationRadiusCrud;
    },
    locationAutoFinishRadius() {
      return this.locationAutoFinishRadiusCrud;
    },
    locationTimeZone() {
      return this.locationTimeZoneCrud;
    },
    locationSupervisor() {
      return this.locationSupervisorCrud;
    },
    locationContacts() {
      return this.locationContactsCrud;
    },
    locationPassword() {
      return this.locationPasswordCrud;
    },
    locationCountersign() {
      return this.locationCountersignCrud;
    },
    locationSecurityPhone() {
      return this.locationSecurityPhoneCrud;
    },
    locationClient() {
      return this.locationClientCrud;
    },
    locationChecklist() {
      return this.locationChecklistCrud;
    },
    newLocation() {
      /*let newChecklists = [];

      this.locationChecklist.forEach((value, index, array) => {
        let newObg = {
          checklist_uuid: value.uuid,
        };

        newChecklists.push(newObg);
      });*/
      let location = {
        name: this.locationName,
        address: this.locationAddress,
        visitsPerMonth: this.locationQuantityVisitsMonth,
        latitude: parseFloat(this.locationLatitude),
        longitude: parseFloat(this.locationLongitude),
        geolocationAdjustment: this.locationGeolocationAdjustment,
        radius: this.locationRadius,
        autoFinishRadius: this.locationAutoFinishRadius,
        timezone: this.locationTimeZone,
        password: this.locationPassword,
        counterPassword: this.locationCountersign,
        securityPhone: this.locationSecurityPhone,
        clientUUID: this.locationClient.uuid,
        contacts: this.locationContacts,
        regionUUID: this.locationRegion.uuid,
        cityUUID: this.locationCity.uuid,
      };

      if (this.locationQuantityVisitsMonth === "") {
        delete location.visits_per_month;
      }

      if (this.locationLatitude === "" || this.locationLatitude === -23.57021) {
        delete location.latitude;
      }

      if (
        this.locationLongitude === "" ||
        this.locationLongitude === -46.64599
      ) {
        delete location.longitude;
      }
      if (this.locationGeolocationAdjustment === "") {
        delete location.geolocationAdjustment;
      }

      if (this.locationRadius === "") {
        delete location.radius;
      }

      if (this.locationAutoFinishRadius === "") {
        delete location.auto_finish_radius;
      }

      if (this.locationAddress === "") {
        delete location.address;
      }

      if (this.locationClient.uuid === null) {
        delete location.client_uuid;
      }

      if (this.locationCity.uuid === null) {
        delete location.city_uuid;
      }

      if (this.locationSupervisor.uuid === 0) {
        delete location.supervisions;
      }

      return location;
    },
  },
  methods: {
    ...mapActions("ModuleErrors", ["changeErrors"]),
    ...mapActions("ModuleSupervision", [
      "changeLocationNameCrud",
      "changeNewLocationRegionCrud",
      "changeNewLocationCityCrud",
      "clearLocationRegionCrud",
      "changeLocationAddressCrud",
      "changeLocationQuantityVisitsMonthCrud",
      "changeLocationLatitudeCrud",
      "changeLocationLongitudeCrud",
      "changeLocationGeolocationAdjustmentCrud",
      "changeLocationRadiusCrud",
      "changeLocationAutoFinishRadiusCrud",
      "changeLocationTimeZoneCrud",
      "changeNewLocationSupervisorCrud",
      "clearLocationSupervisorCrud",
      "changeLocationSpecificContatctCrud",
      "removeLocationSpecificContactListCrud",
      "clearLocationContactCrud",
      "changeNewLocationContactsCrud",
      "changeLocationPasswordCrud",
      "changeLocationCounterPassCrud",
      "changeLocationSecurityPhoneCrud",
      "changeNewLocationClientCrud",
      "clearLocationClientCrud",
      "clearLocationCityCrud",
      "changeNewLocationChecklistCrud",
      "removeLocationSpecificChecklistCrud",
      "clearLocationChecklistCrud",
      "changeLocationDeactivatedCrud",
      "changeCrudCurrentPageTitle",
    ]),
    locationInput(refValue, eventValue) {
      if (refValue === "name") {
        this.changeLocationNameCrud(eventValue);
      } else if (refValue === "region") {
        this.changeNewLocationRegionCrud(eventValue);
      } else if (refValue === "city") {
        this.changeNewLocationCityCrud(eventValue);
      } else if (refValue === "address") {
        let address = eventValue.address;
        let location = {
          lat: eventValue.lat,
          lng: eventValue.lng,
        };

        this.changeLocationAddressCrud(address);
        this.changeLocationLatitudeCrud(location.lat);
        this.changeLocationLongitudeCrud(location.lng);
      } else if (refValue === "newAddress") {
        this.changeLocationAddressCrud(eventValue);
      } else if (refValue === "visits") {
        this.changeLocationQuantityVisitsMonthCrud(eventValue);
      } else if (refValue === "latitude") {
        this.changeLocationLatitudeCrud(eventValue);
      } else if (refValue === "longitude") {
        this.changeLocationLongitudeCrud(eventValue);
      } else if (refValue === "geolocationAdjustment") {
        this.changeLocationGeolocationAdjustmentCrud(eventValue);
      } else if (refValue === "radius") {
        this.changeLocationRadiusCrud(eventValue);
      } else if (refValue === "autoFinishRadius") {
        this.changeLocationAutoFinishRadiusCrud(eventValue);
      } else if (refValue === "supervisor") {
        this.changeNewLocationSupervisorCrud(eventValue);
      } else if (refValue === "contact") {
        let newContact = {
          name: eventValue.one,
          phone: eventValue.two,
        };
        this.changeNewLocationContactsCrud(newContact);
      } else if (refValue === "password") {
        this.changeLocationPasswordCrud(eventValue);
      } else if (refValue === "counterpass") {
        this.changeLocationCounterPassCrud(eventValue);
      } else if (refValue === "securityPhone") {
        this.changeLocationSecurityPhoneCrud(eventValue);
      } else if (refValue === "checklist") {
        let checklistStore = this.locationChecklistCrud;

        if (checklistStore.length === 0) {
          this.changeNewLocationChecklistCrud(eventValue);
        } else {
          let itemReturned = checklistStore.find((value, index, array) => {
            return eventValue.uuid === value.uuid;
          });

          if (itemReturned === undefined) {
            this.changeNewLocationChecklistCrud(eventValue);
          }
        }
      } else if (refValue === "client") {
        this.changeNewLocationClientCrud(eventValue);
      }
    },
    locationSelect(refValue, event) {
      if (refValue === "timeZone") {
        this.enableReticence = true;
        this.changeLocationTimeZoneCrud(event);
      }
    },
    clearStore() {
      this.changeLocationNameCrud("");
      this.changeNewLocationRegionCrud("");
      this.clearLocationRegionCrud();
      this.changeLocationAddressCrud("");
      this.changeLocationQuantityVisitsMonthCrud("");
      this.changeLocationLatitudeCrud("");
      this.changeLocationLongitudeCrud("");
      this.changeLocationGeolocationAdjustmentCrud("");
      this.changeLocationRadiusCrud("");
      this.changeLocationAutoFinishRadiusCrud("");
      this.changeLocationTimeZoneCrud("");
      this.clearLocationSupervisorCrud("");
      this.clearLocationContactCrud();
      this.changeLocationPasswordCrud("");
      this.changeLocationCounterPassCrud("");
      this.changeLocationSecurityPhoneCrud("");
      this.clearLocationChecklistCrud();
      this.clearLocationClientCrud();
      this.clearLocationCityCrud();
    },
    updateContacts(eventValue) {
      this.changeLocationSpecificContatctCrud(eventValue);
    },
    deleteContact(eventValue) {
      this.removeLocationSpecificContactListCrud(eventValue);
    },
    deleteChecklist(checklistIndex) {
      this.removeLocationSpecificChecklistCrud(checklistIndex);
    },
  },
  filters: {},
  watch: {
    errorsList() {
      this.locationError.name = "";
      this.locationError.region = "";
      this.locationError.client = "";
      this.locationError.address = "";
      this.locationError.city = "";
      this.locationError.latitude = "";
      this.locationError.longitude = "";
      this.locationError.timeZone = "";
      this.locationError.role = "";

      this.errorsList.map((error) => {
        if(error.error === "User does not have permission to perform this action") {
          this.locationError.role = `errors.role.unauthorized`;
        }

        if (error.property.indexOf("name") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.name = `errors.name.required`;
          } else if (
            this.hasProperty(error.constraints, "IsLocationUniqueConstraint")
          ) {
            this.locationError.name = `errors.name.uniqueness`;
          }
        }
        if (error.property.indexOf("regionUUID") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.region = `errors.regionId.required`;
          }
        }

        if (error.property.indexOf("clientUUID") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.client = `errors.clientId.required`;
          }
        }

        if (error.property.indexOf("cityUUID") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.city = `errors.city.required`;
          }
        }

        if (error.property.indexOf("address") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.address = `errors.address.required`;
          }
        }

        if (error.property.indexOf("latitude") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.latitude = `errors.latitude.required`;
          }
        }

        if (error.property.indexOf("longitude") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.longitude = `errors.longitude.required`;
          }
        }

        if (error.property.indexOf("clientUUID") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.client = `errors.clientId.required`;
          }
        }

        if (error.property.indexOf("timezone") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.locationError.timeZone = `errors.timeZone.required`;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
.fme-unordered-list__ul {
  list-style-type: none;
  padding-left: 14px;
  li {
    padding-top: 14px;
    .fme-unordered-list__ul--itens {
      .fme-unordered-list__ul--iten {
        padding-top: 22px;
      }
    }
  }
}
.column-future {
  text-align: center;
  border-left: 1px solid $fme-color__old-silver;
  background: rgba(197, 213, 234, 0.15);
  padding-top: 71px;
  h3 {
    font-weight: 800;
    color: $fme-color__yale-blue;
  }
  p {
    font-weight: 500;
  }
}
</style>
